import { Component } from '@verndale/core';
import Player from '@vimeo/player';
import { isFocusable } from '../helpers';

class Module extends Component {
  setupDefaults() {
    this.videoId = this.el.dataset.videoId;
    this.accountId = this.el.dataset.accountId;
    this.isVimeo = this.el.dataset.isVimeo;
    this.ignoreUtilFocusChanges = false;
    this.lastFocus = null;

    this.handleVideoModalClose = this.handleVideoModalClose.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
    this.handleFocus = this.handleFocus.bind(this);

    this.dom = {
      body: document.querySelector('body'),
      link: this.el.querySelector('.video-modal__trigger')
    };
  }

  addListeners() {
    this.el.addEventListener('click', this.handleVideoModalOpen.bind(this));
    this.dom.link.addEventListener(
      'keypress',
      this.handleModalOpenKeyboard.bind(this)
    );
  }

  removeListeners() {
    this.dom.videoModal.removeEventListener(
      'click',
      this.handleVideoModalClose
    );
    document.removeEventListener('keyup', this.handleEscape);
    document.removeEventListener('focus', this.handleFocus, true);
  }

  buildHtml() {
    this.dom.videoModal = document.createElement('div');
    this.dom.videoModal.classList.add('video-modal');
    this.dom.videoModal.innerHTML = `
      <div class="video-modal__inner">
        <div class="video-modal__inner-height">
          <div class="video-modal__video-wrapper">
            <video 
            data-video-id="${this.videoId}"
            data-account="${this.accountId}"
            data-player="default"
            data-embed="default"
            data-application-id
            class="video-js"
            controls></video>
          </div>
        </div>
      </div>
    `;

    this.dom.videoWrapper = this.dom.videoModal.querySelector(
      '.video-modal__video-wrapper'
    );
    this.dom.videoModal.addEventListener('click', this.handleVideoModalClose);
    document.addEventListener('keyup', this.handleEscape);
    document.addEventListener('focus', this.handleFocus, true);

    if (this.isVimeo === 'true') this.setupVimeoPlayer(this.dom.videoWrapper);
  }

  async setupVimeoPlayer(container) {
    container.innerHTML = ' ';
    // If theres no videoId return
    if (!this.videoId) return;
    // Create new vimeo player
    this.vimeoVideoPlayer = new Player(container, {
      id: this.videoId,
      controls: true
    });
  }

  handleVideoModalOpen() {
    this.buildHtml();

    const {scrollY} = window;
    this.dom.body.style.position = 'fixed';
    this.dom.body.style.top = `-${scrollY}px`;
    this.dom.body.classList.add('has-modal');
    this.dom.body.appendChild(this.dom.videoModal);
    this.focusFirstDescendant(this.dom.videoModal);

    const script = document.createElement('script');
    script.setAttribute(
      'src',
      `//players.brightcove.net/${this.accountId}/default_default/index.min.js`
    );
    script.setAttribute('type', 'text/javascript');
    this.dom.videoModal.appendChild(script);
  }

  handleModalOpenKeyboard(e) {
    const key = e.which || e.keyCode;

    if (key === 13 || key === 32) {
      e.preventDefault();
      this.handleVideoModalOpen();
    }
  }

  handleVideoModalClose(e) {
    if (
      e.target === this.dom.videoWrapper ||
      this.dom.videoWrapper.contains(e.target)
    ) {
      return;
    }
    this.removeListeners();

    const scrollY = document.body.style.top;
    this.dom.body.style.position = 'static';
    this.dom.body.style.top = '';
    this.dom.body.classList.remove('has-modal');
    this.dom.body.removeChild(this.dom.videoModal);
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
    this.dom.link.focus();
  }

  handleEscape(e) {
    const key = e.which || e.keyCode;

    if (key === 27) {
      e.stopPropagation();
      this.removeListeners();
      const scrollY = document.body.style.top;
      this.dom.body.style.position = 'static';
      this.dom.body.style.top = '';
      this.dom.body.classList.remove('has-modal');
      this.dom.body.removeChild(this.dom.videoModal);
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  }

  handleFocus(e) {
    if (this.ignoreUtilFocusChanges) return;

    if (this.dom.videoModal.contains(e.target)) {
      this.lastFocus = e.target;
    } else {
      this.focusFirstDescendant(this.dom.videoModal);

      if (this.lastFocus === document.activeElement) {
        this.focusLastDescendant(this.dom.videoModal);
      }
      this.lastFocus = document.activeElement;
    }
  }

  focusFirstDescendant(element) {
    for (let i = 0; i < element.childNodes.length; i++) {
      const child = element.childNodes[i];
      if (this.attemptFocus(child) || this.focusFirstDescendant(child)) {
        return true;
      }
    }
    return false;
  }

  focusLastDescendant(element) {
    for (let i = element.childNodes.length - 1; i >= 0; i--) {
      const child = element.childNodes[i];
      if (this.attemptFocus(child) || this.focusLastDescendant(child)) {
        return true;
      }
    }
    return false;
  }

  attemptFocus(element) {
    if (!isFocusable(element)) {
      return false;
    }

    this.ignoreUtilFocusChanges = true;
    try {
      element.focus();
    } catch (e) {
      return;
    }
    this.ignoreUtilFocusChanges = false;
    return document.activeElement === element;
  }
}

export default Module;
